import React, {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApiNa} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, TripTagInfo} from '@di-shared/api/di/__generated_na';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryManageTagsProps} from '@di-shared/features/history/tags/HistoryManageTags/index';
import {TagFormBottomSheetResult} from '@di-shared/features/history/tags/TagFormBottomSheet/index';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export default function HistoryManageTagsNa({vin}: HistoryManageTagsProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

const MAX_CUSTOM_TAGS = 7;

function Inner({vin}: HistoryManageTagsProps) {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_mytrips_tags_title_eu')}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section">
          <HistoryManageTagsSection key={vin} />
        </section>
      </main>
    </div>
  );
}

function HistoryManageTagsSection() {
  return (
    <ErrorBoundary fallback={<HistoryManageTagsSectionErrorFallback />}>
      <Suspense fallback={<HistoryManageTagsSectionLoading />}>
        <HistoryManageTagsSectionInner />
      </Suspense>
    </ErrorBoundary>
  );
}

function HistoryManageTagsSectionInner() {
  const {t} = useInferableTranslation();
  const {openConfirm} = useCcWebviewUtilsHelper();

  const {data: tags, refetch} = useSuspenseQuery<ApiResponseTripTagListResponse, Error, Array<TripTagInfo>>({
    queryKey: [getQueryKey(diApiNa.tripTags.getAllTags)],
    queryFn: () => diApiNa.tripTags.getAllTags(),
    select: ({data}: ApiResponseTripTagListResponse) => data.tags,
  });

  const numCustomTags = tags.filter(({tagType}) => tagType === 'CUSTOM').length;
  const isCustomTagsLimitReached = numCustomTags >= MAX_CUSTOM_TAGS;

  const [deleteTag] = usePendingHandler(
    async (tagId: string) => {
      const isConfirm = await openConfirm({
        title: t('msg_mytrips_tags_delete_popup_title_eu'),
        message: `${t('msg_mytrips_tags_delete_popup_cannot_restore_eu')} ${t('msg_mytrips_tags_delete_popup_removed_eu')}`,
        okText: t('ctn_mytrips_tags_delete_popup_delete_eu'),
        cancelText: t('ctn_mytrips_tags_delete_popup_cancel_eu'),
      });

      if (!isConfirm) {
        return;
      }

      await diApiNa.tripTags.deleteTag({tagNameOrTagId: tagId});
      await refetch();
      WebviewUtils.showToast(t('ctn_mytrips_tags_delete_toast_complete_eu'));
    },
    async () => {
      WebviewUtils.showToast(t('ctn_mytrips_tags_delete_toast_fail_eu'));
    }
  );

  const [createTag] = usePendingHandler(
    async (tagName: string) => {
      await diApiNa.tripTags.createTag({
        requestBody: {
          tagName,
        },
      });
      WebviewUtils.showToast(t('ctn_mytrips_tags_add_toast_complete_eu'));
      await refetch();
    },
    async (error) => {
      WebviewUtils.showToast(t('ctn_mytrips_tags_add_toast_fail_eu'));
    }
  );

  const [editTag] = usePendingHandler(
    async ({originalTagId, nextTagName}: {originalTagId: string; nextTagName: string}) => {
      await diApiNa.tripTags.renameTag({
        tagNameOrTagId: originalTagId,
        newTagName: nextTagName,
      });
      WebviewUtils.showToast(t('ctn_mytrips_tags_edit_toast_complete_eu'));
      await refetch();
    },
    async (error) => {
      WebviewUtils.showToast(t('ctn_mytrips_tags_edit_toast_fail_eu'));
    }
  );

  async function openCreateTagBottomSheet() {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl<TagFormBottomSheetResult | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_TAGS_FORM.getFullPath()
    );

    if (!result || typeof result === 'boolean' || !result.tagName) {
      return;
    }

    await createTag(result.tagName);
  }

  async function openEditTagBottomSheet(tagId: string, tagName: string) {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl<TagFormBottomSheetResult | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_TAGS_FORM.getFullPath(),
      {
        initialTagName: tagName,
      }
    );

    if (!result || typeof result === 'boolean' || !result.tagName || result.tagName === tagName) {
      return;
    }

    await editTag({
      originalTagId: tagId,
      nextTagName: result.tagName,
    });
  }

  return (
    <>
      <div className="tag_managment">
        <ul className="tag_list">
          {tags.map(({tagId, tagName, tagType}) => (
            <li
              key={tagId}
              className="tag_item"
            >
              <span className="tag_name">{tagName}</span>
              {tagType !== 'BUSINESS' && (
                <div className="tag_actions">
                  <button
                    type="button"
                    className="btn_edit"
                    aria-label="Edit tag"
                    onClick={() => openEditTagBottomSheet(tagId, tagName)}
                  >
                    <span className="ic ic_edit_16px" />
                  </button>
                  <button
                    type="button"
                    className="btn_delete"
                    aria-label="Delete tag"
                    onClick={() => deleteTag(tagId)}
                  >
                    <span className="ic ic_delete_16px" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {isCustomTagsLimitReached ? (
          <p className="desc">
            <span className="ic ic_caution_18px" />
            {t('msg_mytrips_tags_limit_eu')}
          </p>
        ) : (
          <button
            type="button"
            className="btn btn_round btn_round_outline btn_tag_add"
            onClick={() => openCreateTagBottomSheet()}
          >
            <span className="ic ic_add_12px" />
            {t('ctn_mytrips_tags_create_button_eu')}
          </button>
        )}
      </div>
      <HowToTagGuide />
    </>
  );
}

function HistoryManageTagsSectionLoading() {
  return (
    <>
      <div
        className="tag_managment skeleton"
        style={{height: '240px'}}
      />
      <HowToTagGuide />
    </>
  );
}

function HistoryManageTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <div className="tag_managment">
      <div className="trip_info">
        <span className="blind">{t('ctn_mytrips_detail_resize')}</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_tags_failed_to_load_eu') + '\n' + t('msg_mytrips_tags_try_again_eu')}
          </span>
        </div>
      </div>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_mytrips_tags_title_eu')}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section">
          <HistoryManageTagsSectionLoading />
        </section>
      </main>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_mytrips_tags_title_eu')}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section">
          <HistoryManageTagsSectionErrorFallback />
        </section>
      </main>
    </div>
  );
}

function HowToTagGuide() {
  const {t} = useInferableTranslation();
  const [isGuideOpen, setIsGuideOpen] = useState(true);
  return (
    <div className="section_container">
      <div className="driving_accordion type_container">
        <div className="driving_accordion_item">
          <h3>
            <button
              type="button"
              className="driving_accordion_btn"
              aria-expanded={isGuideOpen}
              aria-controls="accordion_content01"
              onClick={() => setIsGuideOpen((prev) => !prev)}
            >
              <span className="driving_accordion_title">{t('msg_mytrips_tags_how_title_eu')}</span>
              <span className="ic ic_arrow_down_16px" />
            </button>
          </h3>
          <div
            className="driving_accordion_content"
            id="accordion_content01"
            style={{display: isGuideOpen ? 'block' : 'none'}}
          >
            <ul className="tag_standard_list">
              <li className="tag_standard_item">
                <div className="tag_standard_icon">
                  <span className="ic ic_company_20px" />
                </div>
                <div className="tag_standard_info">
                  <strong className="tag_standard_title">{t('ctn_mytrips_tags_how_business_eu')}</strong>
                  <span className="tag_standard_desc">{t('msg_mytrips_tags_how_business_eu')}</span>
                </div>
              </li>
              <li className="tag_standard_item">
                <div className="tag_standard_icon">
                  <span className="ic ic_person_24px" />
                </div>
                <div className="tag_standard_info">
                  <strong className="tag_standard_title">{t('ctn_mytrips_tags_how_driver_eu')}</strong>
                  <span className="tag_standard_desc">{t('msg_mytrips_tags_how_driver_eu')}</span>
                </div>
              </li>
              <li className="tag_standard_item">
                <div className="tag_standard_icon">
                  <span className="ic ic_setting_20px" />
                </div>
                <div className="tag_standard_info">
                  <strong className="tag_standard_title">{t('ctn_mytrips_tags_how_custom_eu')}</strong>
                  <span className="tag_standard_desc">{t('msg_mytrips_tags_how_custom_eu')}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
